import axios from "axios";
import { getEffectiveUserId } from "../utils/effectiveUserId";
import { getSecretToken } from "./getSecreteToken";

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** API paths that must not send Authorization (backend allows anonymous access). */
const PUBLIC_API_PATHS = new Set([
  "Object/GetCategories",
  "Object/Recognition",
  "Object/GetObjectTypesByCategoryId",
  "ServiceRequest/Create",
  "ServiceRequest/ConnectToPro",
  "Auth/RegisterUser",
  "Auth/RegisterUserViaThirdParty",
  "Auth/RegisterUserViaThirdPartyApp",
  "Auth/CheckAccountExistence",
  "Auth/WaitingUser",
]);

/** Works with or without a token; never forces token refresh on 401 (avoids full page reload). */
const OPTIONAL_AUTH_API_PATHS = new Set([
  "ServiceRequest/TriggerNotificationToPro",
]);

function normalizeApiPath(urlSegment) {
  if (!urlSegment || typeof urlSegment !== "string") return "";
  return urlSegment.split("?")[0].replace(/^\/+/, "");
}

export function isPublicApiPath(urlSegment) {
  return PUBLIC_API_PATHS.has(normalizeApiPath(urlSegment));
}

function isOptionalAuthApiPath(urlSegment) {
  return OPTIONAL_AUTH_API_PATHS.has(normalizeApiPath(urlSegment));
}

function getBearerAccessToken() {
  const raw = localStorage.getItem("access_token");
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t || t === "null" || t === "undefined") return null;
  return t;
}

function hasRefreshToken() {
  const raw = localStorage.getItem("refresh_token");
  if (raw == null) return false;
  const t = String(raw).trim();
  return !!(t && t !== "null" && t !== "undefined");
}

function withOptionalBearer(urlSegment, headers) {
  const next = { ...headers };
  if (isPublicApiPath(urlSegment)) {
    return next;
  }
  if (isOptionalAuthApiPath(urlSegment)) {
    const token = getBearerAccessToken();
    if (token) {
      next.Authorization = "Bearer   " + token;
    }
    return next;
  }
  const token = getBearerAccessToken();
  if (token) {
    next.Authorization = "Bearer   " + token;
  }
  return next;
}

/** For public GET-style calls, send guest or logged-in user_id_ids when callers omit it. */
function mergeGuestUserIdIntoParamsIfNeeded(urlSegment, method, request) {
  const m = (method || "get").toLowerCase();
  if (m === "post" || m === "put") return;
  if (!isPublicApiPath(urlSegment) && !isOptionalAuthApiPath(urlSegment)) {
    return;
  }
  if (!request.params || typeof request.params !== "object") return;
  const uid = request.params.user_id_ids;
  if (uid == null || uid === "") {
    request.params.user_id_ids = getEffectiveUserId();
  }
}

export const errorGet = (error) => {
  if (error == 410) {
    localStorage.clear()
    window.location.reload();
  }

  return error
}

export async function serviceCallAuth(requestData) {
  // const dispatch =  useDispatch()
  const secretToken = await getSecretToken();
  return new Promise((resolve, reject) => {
    let url = process.env.REACT_APP_BASE_URL + requestData.url;
    let userId = localStorage.userId;
    const request = {
      method: requestData.method || "get",
      headers: {
        "Content-Type": "application/json-patch+json",
        devicetype: "BROWSER",
        "timezone": timeZone,
        "token": secretToken,
      },
      url: url,
    };
    if (requestData.method === "post" || requestData.method === "put") {
      request["data"] = { ...requestData.data } || {};
    } else {
      request["params"] = { ...requestData.data } || {};
    }
    mergeGuestUserIdIntoParamsIfNeeded(
      requestData.url,
      requestData.method,
      request
    );
    axios(request)
      .then((response) => {
        resolve(response);
      })
      .catch((err) => {
        // console.log(err.response.status)
        reject(err);
      });
  });
}
export async function serviceCall(requestData) {
  localStorage.setItem("requestData", requestData);
  const secretToken = await getSecretToken();
  return new Promise((resolve, reject) => {
    let url = process.env.REACT_APP_BASE_URL + requestData.url;
    const request = {
      method: requestData.method || "get",

      headers: withOptionalBearer(requestData.url, {
        "Content-Type": "application/json-patch+json",
        devicetype: "BROWSER",
        timezone: timeZone,
        "token": secretToken,
      }),
      url: url,
    };
    if (requestData.method === "post") {
      request["data"] = { ...requestData.data } || {};
    } else if (requestData.method === "put") {
      request["data"] = { ...requestData.data } || {};
    } else {
      request["params"] = { ...requestData.data } || {};
    }
    mergeGuestUserIdIntoParamsIfNeeded(
      requestData.url,
      requestData.method,
      request
    );
    axios(request)
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        //  console.log(error.response.status)
        //  console.log(error.response.data.status)
        const status = error.response?.status;
        if (status === 401 || status === 406) {
          if (
            hasRefreshToken() &&
            !isPublicApiPath(requestData.url) &&
            !isOptionalAuthApiPath(requestData.url)
          ) {
            callRefreshToken(requestData);
          }
        } else if (status === 429) {
          alert("Too many request, please retry after some time.");
        } else if (status != null) {
          errorGet(status);
        }

        reject(error);
      });
  });
}

export async function callRefreshToken(requestData) {
  if (!hasRefreshToken()) {
    return Promise.resolve();
  }
  const secretToken = await getSecretToken();

  return new Promise((resolve, reject) => {
    const hadAccessToken = !!getBearerAccessToken();
    let url = process.env.REACT_APP_BASE_URL + "Auth/RegenerateToken";

    const request = {
      method: "post",
      url: url,
      headers: {
        "token": secretToken,
        devicetype: "BROWSER",
        timezone: timeZone,
      },
    };

    request["data"] = {
      refresh_token_chr: localStorage.getItem("refresh_token"),
    };

    axios(request)
      .then((response) => {
        localStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("refresh_token", response.data.refresh_token);
        resolve(response);
        if (hadAccessToken) {
          window.location.reload();
        }
      })
      .catch((error) => {

        if (hadAccessToken) {
          localStorage.clear();
          // dispatch(signout())
          // alert("Logged out due to inactivity")
          window.location.reload();
        }

        reject(error);
      });
  });
}

export async function formDataCommonFunction(formData, url) {
  const secretToken = await getSecretToken();
  return new Promise((resolve, reject) => {
    try {
      if (formData instanceof FormData) {
        const p = normalizeApiPath(url);
        if (p === "ServiceRequest/Create" || p === "Object/Recognition") {
          formData.delete("user_id_ids");
          formData.append("user_id_ids", getEffectiveUserId());
        }
      }
      axios
        .post(process.env.REACT_APP_BASE_URL + url, formData, {
          headers: withOptionalBearer(url, {
            "Content-Type": "multipart/form-data",
            devicetype: "BROWSER",
            timezone: timeZone,
            "token": secretToken,
          }),
        })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);

          const status = error.response?.status;
          if (status === 401 || status === 406) {
            if (hasRefreshToken() && !isPublicApiPath(url)) {
              callRefreshToken("rr");
            }
          } else if (status != null) {
            errorGet(status);
          }
        });
    } catch (error) { }
  });
}

export async function formDataCommonFunctionUnAuth(formData, url) {
  const secretToken = await getSecretToken();
  return new Promise((resolve, reject) => {
    try {
      axios
        .post(process.env.REACT_APP_BASE_URL + url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            devicetype: "BROWSER",
            "timezone": timeZone,
            "token": secretToken,
          },
        })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
          const status = error.response?.status;
          if (status === 401 || status === 406) {
            if (hasRefreshToken() && !isPublicApiPath(url)) {
              callRefreshToken("rr");
            }
          } else if (status != null) {
            errorGet(status);
          }
        });
    } catch (error) { }
  });
}

export async function formDataCommonFunctionPut(formData, url) {
  const secretToken = await getSecretToken();
  return new Promise((resolve, reject) => {
    try {
      axios
        .put(process.env.REACT_APP_BASE_URL + url, formData, {
          headers: withOptionalBearer(url, {
            "Content-Type": "multipart/form-data",
            devicetype: "BROWSER",
            timezone: timeZone,
            "token": secretToken,
          }),
        })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
          const status = error.response?.status;
          if (status === 401 || status === 406) {
            if (hasRefreshToken() && !isPublicApiPath(url)) {
              callRefreshToken("rr");
            }
          } else if (status != null) {
            errorGet(status);
          }
        });
    } catch (error) { }
  });
}

export async function formDataFunctionWithoutAuth(formData, url) {
  const secretToken = await getSecretToken();
  return new Promise((resolve, reject) => {
    try {
      axios
        .post(process.env.REACT_APP_BASE_URL + url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            devicetype: "BROWSER",
            "timezone": timeZone,
            "token": secretToken,
          },
        })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
          const status = error.response?.status;
          if (status === 401 || status === 406) {
            if (hasRefreshToken()) {
              callRefreshToken("rr");
            }
          } else if (status != null) {
            errorGet(status);
          }
        });
    } catch (error) { }
  });
}

export async function formDataCommonFunctionPutWithoutAuth(formData, url) {
  const secretToken = await getSecretToken();
  return new Promise((resolve, reject) => {
    try {
      axios
        .put(process.env.REACT_APP_BASE_URL + url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            devicetype: "BROWSER",
            "timezone": timeZone,
            "token": secretToken,
          },
        })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
          const status = error.response?.status;
          if (status === 401 || status === 406) {
            if (hasRefreshToken()) {
              callRefreshToken("rr");
            }
          } else if (status != null) {
            errorGet(status);
          }
        });
    } catch (error) { }
  });
}
